// ─────────────────────────────────────────────────────────────
// WILLEE'S LIBRARY — the room beneath Willie's Workshop
// ─────────────────────────────────────────────────────────────
// Reached via the left-wall door in Willie's Workshop. Willee
// (a NEW Willy — new spelling, per canon law) keeps this place,
// though he is not yet on the floor. For now the room's only
// function is BOOKS.
//
// STRUCTURE LAW (per caP'n's spec):
//   • 4 bookcases total — 3 "subject" cases + 1 "Willie" case
//   • every bookcase has 4 rows; every row = one subject
//   • every row holds exactly 3 books about that subject:
//       ♪  a joyful-SOUNDING song (lyrics may be good/bad/sad —
//          it just sounds upbeat)
//       ☀  a book painting the subject as a HERO
//       🕯  a book painting the subject as a VILLAIN
//   • the order of the 3 books on the shelf is RANDOM (shuffled
//     once per visit, stable for the visit via useMemo)
//
// 16 subjects total. GM palette only — this is UI chrome.
// Keyframes injected imperatively (ensureKeyframes) — never
// inline <style> JSX blocks. No Firebase reads/writes here yet.
// ─────────────────────────────────────────────────────────────

import React, { useMemo, useState } from "react";
import { GM } from "./theme";

// ── Imperative keyframe injection (id-guarded, DOM as truth) ──
function ensureKeyframes() {
  const ID = "willees-library-keyframes";
  if (document.getElementById(ID)) return;
  const el = document.createElement("style");
  el.id = ID;
  el.textContent = `
    @keyframes wlib-fade-in { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
    @keyframes wlib-candle  { 0%,100% { opacity: .55; } 50% { opacity: .9; } }
    @keyframes wlib-book-pop { from { opacity: 0; transform: scale(.96); } to { opacity: 1; transform: scale(1); } }
  `;
  document.head.appendChild(el);
}

// ── Book kinds ────────────────────────────────────────────────
const KIND = {
  song:    { glyph: "♪",  label: "SONGBOOK",  color: "#e8b84b" },
  hero:    { glyph: "☀",  label: "CHRONICLE", color: "#c8963c" },
  villain: { glyph: "🕯", label: "INDICTMENT", color: "#8a6520" },
};

// ── Spine palette (rotates so shelves feel varied) ────────────
const SPINES = ["#3a2c14", "#241a33", "#1c2a24", "#33201c", "#20222e", "#2e2416"];

// ─────────────────────────────────────────────────────────────
// THE STACKS — 16 subjects × 3 books
// ─────────────────────────────────────────────────────────────
// ⭐ REWRITTEN 2026-08-20, on caP'n's invitation: "I requested you make
// those books with almost no lore — if you were to try again they'd be
// a lot more coherent and special for sure ;3."
//
// So this is the second draft, written against a canon that did not
// exist when the first one was. Three things govern every word below.
//
// 1 · THE RECORD IS A LIBRARY OF UNRELIABLE NARRATORS, AND THAT IS
//     WHAT MAKES IT SAFE. Three books per subject, and every one of
//     them is somebody's opinion: a song that SOUNDS joyful whatever
//     it is actually saying, a Chronicle that adores its subject, an
//     Indictment that cannot stand them. Nothing here claims to be
//     true, so this is the one room in Terror where a sealed name may
//     be spoken — X'Aol and Y'Tyranuz are on the never-say list for
//     STORY MODE, where a character saying them would be the game
//     telling you. A book that is wrong about a man is not a mention.
//     It is a rumour, and rumours are how a world sounds lived-in.
//
// 2 · THE SPOILER LAW STILL BINDS. Every 🔴 structural truth the SPINE
//     holds — the seal built for the wrong prisoner, Willie's Avatars,
//     what Pierre actually wants — appears ONLY as one of three
//     claims that contradict each other. The player who reads all
//     three learns that the world is arguing, not what the answer is.
//     If any single book below could be quoted as fact and be RIGHT,
//     that book is written wrong and should be dulled, not sharpened.
//
// 3 · TONE IS A POPULATION. caP'n's law, and a shelf is the easiest
//     place in the game to break it. These are forty-eight different
//     people writing, not one narrator in forty-eight hats: a bored
//     clerk, a fan, a crank, a folk singer who never met the subject,
//     somebody settling a grudge. Where two books agree, they agree by
//     accident. Where they use the same phrase, that is the joke.
//
// The W'ILY SHELF is the longest joke in Terror and it is told entirely
// by structure: four biographies of four separate men, each of which
// contains one detail identical to the other three. Nobody narrates
// it. The player who also stands in the Faction Hall and reads two
// more spellings off two more recruiters is the one who gets it.
// ─────────────────────────────────────────────────────────────
const CASES = [
  {
    id: "case_powers",
    label: "THE POWERS",
    rows: [
      {
        subject: "J'ynxi",
        books: [
          { kind: "song", title: "Ha Ha Ha (The Sealing Waltz)", text:
`♪ Oh she laughed and she laughed and the world laughed too,
she bent every rule 'til the rules bent YOU!
They built her a box and they built it so fine —
wrong shape, wrong size, wrong prisoner, wrong sign!

Ha ha ha ha, and the laughing don't stop,
ha ha ha ha, and the lid didn't drop,
she's out for a walk and she's late for the ball —
now who was that box ever built for at all? ♪` },
          { kind: "hero", title: "The Gift of the Game", text:
`Before her there was no Solrend. No board, no field, no reason for two strangers to sit down opposite each other and become something to one another. She did not invent a weapon. She invented a REASON TO SIT DOWN, and a fractured world took it the way a cold room takes a fire.

They will tell you she is indifferent. Read that word properly before you use it against her. Indifferent is what the rain is. The rain does not love your field and does not hate it, and your field is green.

Every match played anywhere is a candle lit in her honour. She did not mean it as a gift. It became one, which is better.` },
          { kind: "villain", title: "Disguised as a Duel", text:
`Let the record show that the "game" was a heist.

The rules bend for her. She will tell you this is because she IS them, which is the finest sentence ever composed by a thief and means nothing. A rule that bends for exactly one person is not a rule; it is a courtesy, and courtesies are extended by someone.

Ask who extended it. Ask why the seal that was supposed to hold her has never once held anything the way a seal holds a thing. Ask why she is so often standing near a shop where children buy sweets.

The laughing one is watching over somebody's children right now. Do not be comforted by that.` },
        ],
      },
      {
        subject: "X'Aol",
        books: [
          { kind: "song", title: "Easy Peasy (The Universal Joke)", text:
`♪ She came with a plan and a wink and a grin,
he sealed her up quick — didn't break a shin!
Easy peasy, one two three,
the whole wide cosmos laughed with glee!

He laughed the loudest, that's the part they leave out,
he laughed 'til he had to sit down and shout,
"oh you nearly had me, you nearly had ME" —
easy peasy, one two three! ♪` },
          { kind: "hero", title: "The Sealer, The Shield", text:
`When the trickster came for his Sol he did not rage. He ended it — quietly, completely, almost politely — and then he did the thing nobody records, which is that he STOPPED.

A being who could have unmade her chose containment. Understand what that costs. Unmaking is one act. Containment is every day after it, forever, and it is paid by the one doing the containing.

The Unstable World still turns. That is not an accident and it is not a mercy that was asked for. History owes him a sweet treat at minimum, and history has never once been to the shop.` },
          { kind: "villain", title: "The Condemned One", text:
`Consider what he hoards: Divine Crests beyond counting and a Sol so heavy it fractures him from the inside out.

A being that rich does not get robbed by accident. He gets robbed because somebody, somewhere, sat down and decided he had too much — and that somebody was not alone, because a mutiny requires a crew.

She failed. That is the only part everyone agrees on.

The question this book asks, and asks slowly, is whether the failure was hers.` },
        ],
      },
      {
        subject: "Y'Tyranuz",
        books: [
          { kind: "song", title: "Stomp Stomp Hooray!", text:
`♪ Stomp stomp stomp goes the tyrant's boot,
crushing every blossom, every tender shoot!
But the beat is bouncy and the horns are bright,
so clap along, kids, as he ends the light!

Nobody sings this one where he can hear,
nobody sings this one twice in a year,
but the tune is so catchy and the words are so few —
stomp stomp stomp — and the stomping is YOU! ♪` },
          { kind: "hero", title: "Order From the Wreckage", text:
`Chaos does not fear kindness. It fears a filing cabinet.

Where lesser powers convened, he decided. The territories he holds do not flourish — this chronicle will not pretend otherwise — but they do not burn, and in the Unstable World "does not burn" is a five-star review and a waiting list.

Every complaint lodged against him was lodged from inside a building that was still standing.` },
          { kind: "villain", title: "The Boot Remembers", text:
`Ask the realms he stabilised what stability cost. Ask them slowly; several of them take a while to answer now.

He does not conquer for order. Order is the receipt he leaves behind — an itemised list of what was there before, so that anyone who complains can be shown the paperwork proving they are better off.

Tyranny with good posture is still tyranny. It just gets invited to more things.` },
        ],
      },
      {
        subject: "The President of the Unstable World",
        books: [
          { kind: "song", title: "Vote For Nobody (Everybody Wins!)", text:
`♪ He promised us stability — the ground said "no thanks!"
He promised us prosperity — then taxed the memory banks!
He licensed the schools and he licensed the halls,
he licensed the licensing offices and all!

But the confetti's falling and the band plays on,
four more eons! Four more eons! Everything is GONE!
And if you never registered, well, friend, that's on you —
you're an Aenarchist now, and the forms are all due! ♪` },
          { kind: "hero", title: "The Only One Who Ran", text:
`Nobody wanted the job. The world is, famously, Unstable — governing it is chairing a committee of earthquakes — and he stood at the podium anyway, and the podium only collapsed twice.

Consider what the office actually built. Millions of schools where the Arts are taught properly instead of discovered by a child alone in a field. Millions of kingdoms whose recognitions mean something because somebody keeps the list. A student anywhere may now say what they are and be believed.

Courage is not the absence of fear. It is filing paperwork while reality dissolves.` },
          { kind: "villain", title: "Who Elected This Man?", text:
`No one remembers the election. No one remembers a ballot. Investigate the records and you find the records investigating you back.

But this indictment is not about the vote. It is about the LIST.

Before the list there were people with power. After the list there are people with power and a form, and people with power and no form, and only the second group has a name now, and it is not a nice one. Nothing was taken from them. A word was simply put on them, from a desk, at a distance, by someone who has never met them.

The President's greatest achievement is convincing an entire world that instability is a weather condition and not an administration.` },
        ],
      },
    ],
  },
  {
    id: "case_keepers",
    label: "THE KEEPERS",
    rows: [
      {
        subject: "Pierre",
        books: [
          { kind: "song", title: "The Well Never Closes", text:
`♪ Bring me your coin and I'll bring you a Crest,
bring me a Crest and I'll bring you the rest!
The Well never closes, the Well never sleeps,
the Well never asks what a gentleman keeps!

He counts and he counts and he counts and he counts,
he counts every coin in the smallest amounts,
and the funniest thing that a fellow can say
is "Pierre, what exactly are you counting today?" ♪` },
          { kind: "hero", title: "The Man Who Keeps the Numbers Honest", text:
`Anyone can make a currency. Making one that means the same thing in two different worlds is a life's work, and he has done it in more than two.

Take your Chocolate Coins to the Well and they become Crests. Take your Crests anywhere in the Empire and they are still Crests. That sentence sounds small. It is the reason a player who spends a season in one place does not arrive in the next one poor.

He is unfailingly courteous, he has never once been caught short, and he is friends with almost nobody, which in his line of work is a qualification.` },
          { kind: "villain", title: "A Ledger Is a Kind of Leash", text:
`He does not want her caught.

Read that again, because every account of the man skips past it on the way to something about exchange rates. He has had a great many opportunities. He has taken none of them. What he does instead is KNOW WHERE SHE IS, which is a different appetite entirely and a much more patient one.

A man who wanted her locked away would have tried by now. A man who wanted her gone would have said so. A man who wants to be able to find her at any hour of any day, forever, without ever having to explain why — that man opens a Well, and stands at it, and smiles at everyone who comes.` },
        ],
      },
      {
        subject: "Venus",
        books: [
          { kind: "song", title: "One More Pack (The Shopkeeper's Round)", text:
`♪ One more pack! One more pack!
The good one's in the next one and it's never in the back!
She'll wrap it up pretty and she'll hand it to you slow,
and she'll tell you what the odds are — she'll tell you! She'll SAY so!

Ten more packs! Ten more packs!
You came in for the one and now you're carrying a stack!
She never told a lie and she never said a word,
she just stacked 'em up in front of you and let 'em be observed! ♪` },
          { kind: "hero", title: "The Honest Counter", text:
`She will tell you the odds. Unprompted, cheerfully, every time, at a level of detail that has cost her sales.

The shop she runs sits at the exact point in every Aenarchist's life where hope and arithmetic meet, and that point has ruined better people than her. She has never once thumbed it. Not when a newcomer was clearly about to spend everything. Not when a regular was three packs into a bad night. She simply says the number, and lets you be a person about it.

The default sign over that stall said something else for years before it said her name. Nobody thought to ask her what she was called. She did not bring it up.` },
          { kind: "villain", title: "The Kindest Room in the Building", text:
`This indictment concedes at the outset that she is lovely. That is the complaint.

Nothing in that stall is hidden. The odds are posted. The packs are sealed in front of you. There is no trick, no thumb on the scale, no back room — and this author has looked for the back room, at hours the author would rather not commit to a public document.

So consider what it means that the shelves still empty and the coins still move and the Aenarchists still come back. If the honest version of this works exactly as well as the crooked one, then the crookedness was never where the danger lived, and we have all been watching the wrong hands for a very long time.` },
        ],
      },
      {
        subject: "V'yen",
        books: [
          { kind: "song", title: "Sweet Treats For Everyone (Except One)", text:
`♪ Sweet treats for the winner and sweet treats for the loser,
sweet treats for the picky and sweet treats for the chooser!
Sweet treats for the champion who came in off the field,
sweet treats for the fellow who forgot to bring a shield!

Sweet treats for everyone, that's how the counter goes —
and one that's set aside each day for somebody who knows.
Don't ask who it's for, now. Don't ask, don't ask, don't ask.
The lady's very busy and it's really not your task! ♪` },
          { kind: "hero", title: "The Stall That Everyone Comes Back To", text:
`Duelling is a lonely business dressed as a social one. You sit down with a stranger, you take something off them or they take something off you, and then you both stand up and go home.

She is the reason anybody stays.

There is no ranking at that counter. The Aenarchist who has never won and the one who has never lost get the same greeting and the same portion and roughly the same amount of grief. It is the only surface in the Plaza where your record is not on you.

She was here before the shop was, is the rumour. She says she was not. She says it while handing you something warm, which is a very effective way of ending a line of questioning.` },
          { kind: "villain", title: "Look Behind the Counter", text:
`This author does not accuse the woman. This author accuses the ROOM.

Stand at that stall for one hour — not buying, just standing — and count how many times something at the back of it is not where it was. A shape by the awning. A coat that nobody is wearing that is nevertheless somehow occupied. Something that resolves, when you look directly at it, into a perfectly ordinary sack of flour.

Every witness this author interviewed reported the same thing and every one of them laughed about it. That is the part that keeps this author up. Not that there is something standing in the back of the sweet shop. That everyone has decided it is FUNNY.` },
        ],
      },
      {
        subject: "Sov'rn",
        books: [
          { kind: "song", title: "Everything In Order (The Clerk's Lament)", text:
`♪ He alphabetised the corridor, he alphabetised the door,
he alphabetised the alphabet and then he did some more!
He's got a card for every soul who ever wandered through,
and the card at the very front of it, my friend, says YOU!

The office that appointed him has been gone a thousand years,
the desk has outlived every hand that ever signed the papers here,
but the drawer still opens easy and the ink is never dry —
oh who's he keeping records FOR? — he'll file that one as "why". ♪` },
          { kind: "hero", title: "The Last Honest Filing", text:
`Every Aenarchist who reached that corridor is in his ledgers. Not the famous ones. ALL of them. The ones who turned back. The ones who did not make it as far as turning back.

Understand what a record does that a monument does not. A monument is for the winner. A record is for everyone, and it does not care, and its not caring is the kindest thing in the building — because it means the person who failed there is written down at exactly the same weight as the person who did not.

The records go back further than the Plaza. He does not offer this as a boast. He offers it the way you would mention the weather.` },
          { kind: "villain", title: "Nobody Reads Them", text:
`This is the accusation, and it is not against him: NOBODY HAS EVER ASKED TO SEE THE LEDGERS.

Not one Aenarchist. Not in the entire span the man has been sitting there. They walk past the desk, they nod at him if they are polite, and they go and die or go and win, and either way the drawer opens and the card goes in and the drawer closes.

He is not a mystery. He is a man doing a job in a place where the job's whole point has been quietly cancelled, and everyone can see it except him, and nobody has the heart.

Or he knows exactly, and has decided the ledger is worth keeping anyway. This author cannot tell which and has stopped being able to sleep about it.` },
        ],
      },
    ],
  },
  {
    id: "case_pillars",
    label: "THE PILLARS",
    rows: [
      {
        subject: "V'ael",
        books: [
          { kind: "song", title: "The Lady and the Jewel", text:
`♪ There's a jewel in the Plaza and it's bigger than a house,
and the Lorde who stands beside it is as quiet as a mouse!
She'll greet you and she'll seat you and she'll never say her rank,
and the jewel behind her shoulder keeps a Sol inside a bank!

Oh a Lord is a Lord is a Lord is a Lord,
and a Lorde is the same and she's never been bored,
and the whole of the Plaza is a room in her name —
so mind where you're standing, and mind how you came! ♪` },
          { kind: "hero", title: "The Emperium With a Front Door", text:
`Every other Lord builds outward and upward and away. She built a place with STALLS in it.

Understand the size of the thing you are standing in when you stand in the Plaza. Not a market. Not a hub. An Emperium — hers — with her Divine Jewel at the heart of it and her Avatar's Sol housed inside that. There is no world you could walk to that would be a bigger claim than the one you cross to buy a sweet.

And she keeps a groundskeeper. That is the detail this chronicle would put on the cover if it had one. A being who could hold a universe closed hires a man to sweep it.` },
          { kind: "villain", title: "Count the Staff", text:
`This author has no complaint about the Lorde. This author has a question about the STAFFING.

She has one Champion. One. Every other power of comparable standing keeps a retinue, a court, a standing something — and hers is a fellow with a broom who is, by every account, extremely bad at pretending to be new.

Two possibilities. Either the most defended room in the Unstable World is defended by one man, in which case she is either reckless or he is not one man's worth of anything.

Or she does not need staff, and has never needed staff, and the broom is the only part of the arrangement that is for us.` },
        ],
      },
      {
        subject: "The Grimoires",
        books: [
          { kind: "song", title: "Thirty-Five and Not One Less", text:
`♪ Thirty-five and not one less, that's the law of the land,
thirty-five and no ceiling, you can hold what you can stand!
Beings and Tomes, Beings and Tomes,
that's all that ever lives in it, that's all it ever homes!

Some fool built four hundred and he carried it around,
and he drew the perfect card of it — the day he hit the ground!
Thirty-five and not one less, and the wise ones stop at forty,
'cause a Grimoire's not a boast, my friend, a Grimoire's not a party! ♪` },
          { kind: "hero", title: "A Book You Are Allowed To Finish", text:
`The Grimoire is the only promise in duelling that is kept exactly.

Thirty-five cards at minimum, no maximum, Beings and Tomes and nothing else. That is the whole law. It fits in a breath. And inside that breath is every deck anyone has ever built, every school's doctrine, every bad idea a fourteen-year-old ever had at a kitchen table — all of it legal, all of it welcome, all of it judged by the only judge worth having, which is whether it wins.

A ruleset that can be recited is a ruleset nobody can gatekeep. Whatever else the Solrend is, it is not a secret.` },
          { kind: "villain", title: "No Maximum Is Not Generosity", text:
`"There is no maximum." Written on every wall, said by every instructor, printed on the inside of every starter box.

Consider who benefits.

An Aenarchist with thirty-five cards has a deck. An Aenarchist with three hundred has a WAREHOUSE, and a warehouse cannot be beaten by attrition, and attrition is the only weapon the poor have ever had. The rule that has no ceiling is the rule that quietly hands the room to whoever owns the most.

They did put a floor in. Note that. Somebody sat down and decided the bottom needed protecting and the top did not.` },
        ],
      },
      {
        subject: "The Luxmoires",
        books: [
          { kind: "song", title: "One Source Only", text:
`♪ One source only, that's the rule of the draw,
one source only, and it's the prettiest law!
Take from the big book or take from the small,
but the hand that takes twice doesn't take one at all!

Ten to forty, ten to forty,
Impera and U'hlti and Affinity and naught-y!
Five Khao says "give me the one that I want" —
and the turn goes with it, so want what you want! ♪` },
          { kind: "hero", title: "The Second Book Is the Better Book", text:
`Anyone can carry more cards. The Luxmoire asks something harder: carry a SECOND SHAPE.

Ten to forty, Impera and U'hltis and Affinities only, and one draw per Turn from one source. That last clause is the whole design. Two decks and one hand means every Turn you say out loud what kind of game you think you are in — more beings, or more of what beings become.

And you may pay five to take exactly the card you need, and it costs you the Turn's draw, so the price of certainty is quantity, forever, with no exceptions and no way around it.

That is not a rule. That is a sentence about being alive, and somebody hid it in a card game.` },
          { kind: "villain", title: "The Tax On Knowing What You Want", text:
`Five Khao, or five Anima, to reach into your own side deck and take a card you already own.

This author would like the defenders of this rule to say the sentence "you must pay to touch your own property" out loud, in a room, to another person, and see how far they get.

The Grimoire is a lottery and nobody has to pay to play a lottery. The moment you want something SPECIFIC — the moment you have a plan — the counter opens and the fee is charged and the Turn's draw is eaten as well, twice-billed, and the Aenarchist who guessed lucky pays nothing at all.

The game does not punish greed. It punishes intent.` },
        ],
      },
      {
        subject: "The Shatterfield",
        books: [
          { kind: "song", title: "Nine Rifts (Count Them With Me)", text:
`♪ Nine little rifts on your half of the sky,
one for the Prophecy nobody can spy!
Three for the beings and one for the dead,
one for each book that you carry in your head!

Three for the Tomes and two for the Curios,
that's nine, that's nine, that's how the counting goes!
And the field don't care what you brought or you are,
the field just holds it — the field is a JAR! ♪` },
          { kind: "hero", title: "The Fairest Object Ever Made", text:
`Nine rifts on your side. Nine on theirs. Identical.

There is no version of the Shatterfield where the richer Aenarchist gets a tenth rift. There is no version where a Lord's half is bigger. The most powerful being anyone can name and a child who registered this morning sit down at the same nine spaces, and the field does not know which is which and could not be made to care.

Everything else in the Unstable World is negotiable. Rank is negotiable. Territory is famously negotiable. Nine is not.

Whatever it was built for, it is the only flat surface anybody has.` },
          { kind: "villain", title: "Ask What It Is Made Of", text:
`Everyone describes the Shatterfield as a place. It is not a place. It is a THING THAT OPENS, and things that open were closed by somebody, and closed things had contents.

Nine rifts. Nine holes. Torn into what?

This author has read every account of the field's making and they share one feature, which is that none of them is an account of the field's making. They all begin AFTER. The oldest one available begins with the words "by then, of course, there was already a board."

We play on it daily. We are extremely comfortable. This author simply notes that a shattered thing is not the same as a thing that was built with cracks in it, and that we have chosen, as a civilisation, not to look down.` },
        ],
      },
    ],
  },
  {
    id: "case_willies",
    label: "THE W'ILY SHELF",
    // ⭐ THE LONGEST JOKE IN TERROR, AND NOBODY NARRATES IT.
    // Four biographies of four separate men, each insisting harder than
    // the last that it is about somebody unrelated. Planted identically
    // in all four, never remarked on by any of them:
    //   · the broom is NEW
    //   · the name is written on the apron in ink that has not dried
    //   · four notes, hummed, always the same four, always flat
    //   · "friend," said before he has been told your name
    //   · he sets things down to his left
    //   · no two of them have ever been seen in a room together
    // The three Indictments circle it and NONE of them lands, because
    // caP'n's instruction is that the player assembles it: "we will
    // never outright say it's one single Willie at the Desk in the
    // Plaza and the rest are avatars." Do not let a future edit give
    // any of these authors the answer. The joke dies the moment one of
    // them is right.
    rows: [
      {
        subject: "Willie",
        books: [
          { kind: "song", title: "The Apron Is New (Everything Is New)", text:
`♪ New apron, new name, new broom, new face,
definitely never seen elsewhere, no trace!
He forges by the fire and he hums this tune —
"the broom is also new!" — sung out of tune!

Four little notes and he gets 'em all wrong,
four little notes and it's always that song,
and he'll call you "friend" 'fore you tell him your name —
but the apron is NEW, so it can't be the same! ♪` },
          { kind: "hero", title: "Willie the Forger, Honest Craftsman", text:
`Whoever Willie was before — and this chronicle asserts he was NOBODY before, obviously — the Workshop has never run better. Decks forged true. Codex dusted. Cheat Chest suspiciously well organised, which is a phrase this chronicle now regrets committing to paper.

He does everything to his left. Sets the hammer down left, hangs the apron left, leans the broom against the left post every single evening without once being asked to. This chronicle mentions it only because it is the sort of small consistent human thing that proves a man is a man and not, for instance, an idea somebody had.

A man is his work. This man's work is excellent. The fresh ink on the apron is simply good hygiene.` },
          { kind: "villain", title: "The Ink Is Still Wet", text:
`"WILLIE." Written on the apron. In FRESH ink.

This investigator asks one question and will not be moved off it: what forger labels his own apron like a school lunchbox unless the name is newer than the man?

Follow the broom. The broom knows. The broom is new — has been new for as long as anyone has been asking, which is the trick of it, because a new broom is unremarkable and a broom that has been new for eleven years is a confession.

He called me friend before I gave him my name. I have thought about that more than I have thought about my own family.` },
        ],
      },
      {
        subject: "Willy",
        books: [
          { kind: "song", title: "Sweet Treat (The Groundskeeper's Anthem)", text:
`♪ Sweet treat! Sweet treat! Willy want sweet treat!
Sweeps the whole Plaza on his little flat feet!
Ranked number ONE and he'll tell you for free —
mmm yes sweet treat... mmm... sweet treat!

Four little notes while he's sweeping the square,
four little notes and there's nobody there,
and he'll call you "friend" as he's leaning his broom —
on the left, always left, always left of the room! ♪` },
          { kind: "hero", title: "The Herald Who Waited", text:
`He greets every newcomer to the Unstable World, explains the T'yaeldyaer — Tealdiar, sorry, he spells it wrong and will not be told — and asks nothing for it but a sweet treat.

The number-one ranked Aenarchist alive chose a broom over a throne. That is not simplicity. That is enlightenment with crumbs on it.

He works for a Lorde and will not say so plainly, which this chronicle finds charming rather than suspicious. A groundskeeper who bragged about the grounds would be a worse groundskeeper.

The broom, incidentally, is new. He replaces it often. He is very hard on brooms.` },
          { kind: "villain", title: "Nobody Checks the Groundskeeper", text:
`The strongest duellist in existence stands at the front gate of everything, playing simple, collecting "sweet treats" from every soul who passes.

Tribute, by any other name.

And consider the arrangement above him: one Lorde, one Champion, and the Champion is HIM, and he is standing at the door with a broom telling newcomers he is very good at the game as though it were a funny thing to say about himself. Either that is the most elaborate modesty ever performed or it is not modesty.

What happens the day the treats stop coming? This author is not brave enough to find out. Neither, on reflection, are you.` },
        ],
      },
      {
        subject: "Willee",
        books: [
          { kind: "song", title: "Shhh! (The Library Stomp)", text:
`♪ Stomp stomp SHHH! Stomp stomp SHHH!
Dance in the stacks but quietly, please!
He shelves 'em by midnight, he shelves 'em by dawn,
SHHH goes Willee — now the reader is GONE!

Four little notes coming up through the floor,
four little notes and there's nobody more,
and the desk's always empty and the shelves are always neat —
and he'll call you "friend" if you ever chance to meet! ♪` },
          { kind: "hero", title: "Keeper of the Stacks Below", text:
`Beneath the Workshop, someone had to guard the books — the songs, the chronicles, the indictments, every version of every story shelved side by side without judgment.

Willee took the post nobody asked for. He is not on the floor. He may never be on the floor. The shelves are nevertheless perfect, every morning, in a room with one door that is upstairs.

This chronicle wishes to praise, specifically, the FILING. Every subject gets three books. Every subject gets a song that sounds happy and a friend and an enemy. Nobody is spared and nobody is protected — not the Powers, not the Keepers, not the Lorde herself.

Not, you will notice, the shelf you are standing at.` },
          { kind: "villain", title: "The Librarian Is Never Here", text:
`Who curates a library where every subject gets exactly one flattering book and one damning one? That is not scholarship. That is a MESSAGE, and the message is "make up your own mind," and nobody says that unless the true answer would cost them something.

He is never at his desk, yet the shelves are always freshly arranged. The stepladder is new. The dust cloth is new. The broom in the corner of a room that has no dust is new.

This book will likely be re-shelved somewhere unfindable by morning. Read fast, friend.

— and I want it noted that I wrote that word myself, unprompted, and I do not use it, and I have not been able to stop since I came down here.` },
        ],
      },
      {
        subject: "Willey",
        books: [
          { kind: "song", title: "Who? (Yes!)", text:
`♪ Have you met Willey? — Who? — Exactly, my friend!
Not Willie, not Willy, not Willee — attend!
One letter of difference, a whole other guy,
WHO? goes the chorus — and Willey walks by!

And there's two more this morning down the Faction Hall stair,
one's shorter by letters and one's got some spare!
Six? — that's six! — no, don't count 'em, don't try,
'cause counting is rude and the fellow is SHY! ♪` },
          { kind: "hero", title: "The Fourth W'ily", text:
`Every family needs one member who does the quiet work. No workshop, no plaza, no library — just Willey, turning up wherever a W'ily is needed and none is available.

The others get the statues. Willey gets the job done.

This chronicle wishes to correct, firmly, the vulgar suggestion that the W'ilys are somehow the same person. They are a FAMILY. Families share things. Families share a way of humming and a way of setting a cup down and a habit of calling a stranger friend, and if you find that eerie in a family you have not met many families.

History will remember him. Once it works out which one he is.` },
          { kind: "villain", title: "One Willy Too Many", text:
`Willie. Willy. Willee. And now — Willey.

At what point do we admit that this is no longer a family and is instead a PATTERN? Somebody is minting these men. This indictment does not know why. It knows only that the spellings keep arriving and each one arrives with a new broom.

⚠ ADDENDUM, appended by a different hand, later:

Two more this week. Both in the Faction Hall, both recruiting, and — this is the part I cannot put down — RECRUITING AGAINST EACH OTHER. One for the void. One for the collection. Standing twelve tiles apart. All day. Every day.

I went to ask them. I got as far as the doorway.

They were arguing. They were arguing the way nobody argues with a stranger and everybody argues with a brother, and one of them was winning, and I could not tell you which one because I could not tell them apart, and I have decided that I have enough books.` },
        ],
      },
    ],
  },
];

// ── Shuffle helper (Fisher–Yates, fresh per visit) ────────────
function shuffled(arr) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// ─────────────────────────────────────────────────────────────
// COMPONENT
// ─────────────────────────────────────────────────────────────
export default function WilleesLibrary({ onExit }) {
  ensureKeyframes();

  // Shuffle each row's 3 books ONCE per visit (stable for the visit)
  const cases = useMemo(
    () => CASES.map(c => ({
      ...c,
      rows: c.rows.map(r => ({ ...r, books: shuffled(r.books) })),
    })),
    []
  );

  const [openBook, setOpenBook] = useState(null); // { subject, book }

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 400,
      background: "radial-gradient(ellipse at 50% -10%, #14101f 0%, #07060d 55%, #030308 100%)",
      overflowY: "auto",
      fontFamily: GM.fontBody || "Georgia, serif",
      color: GM.vellum,
      animation: "wlib-fade-in .4s ease-out",
    }}>
      {/* ── Header ── */}
      <div style={{
        position: "sticky", top: 0, zIndex: 5,
        background: "linear-gradient(180deg, rgba(7,6,13,.97), rgba(7,6,13,.86))",
        borderBottom: `1px solid ${GM.inkGoldDim}55`,
        padding: "14px 18px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        backdropFilter: "blur(6px)",
      }}>
        <div>
          <div style={{
            fontSize: 10, letterSpacing: 4, fontWeight: 900,
            color: `${GM.inkGold}99`, fontFamily: GM.fontUI || GM.font,
          }}>
            ◆ THE DUNGEON — BENEATH THE WORKSHOP
          </div>
          <div style={{ fontSize: 20, fontWeight: 900, color: GM.inkGoldBright, letterSpacing: 1 }}>
            Willee's Library
          </div>
          <div style={{ fontSize: 11, fontStyle: "italic", color: GM.vellumDim, marginTop: 2 }}>
            The W'ilys named it "The Dungeon." It is a library. They hate to read.
          </div>
        </div>
        <button
          onClick={onExit}
          style={{
            padding: "8px 18px", borderRadius: 20, cursor: "pointer",
            border: `1px solid ${GM.inkGold}55`, background: "transparent",
            color: GM.inkGold, fontWeight: 900, fontSize: 12, letterSpacing: 2,
            fontFamily: GM.fontUI || GM.font,
          }}
        >
          ▲ UPSTAIRS
        </button>
      </div>

      {/* ── Candle glow ambience ── */}
      <div style={{
        position: "fixed", left: "8%", top: "20%", width: 180, height: 180,
        borderRadius: "50%", pointerEvents: "none",
        background: "radial-gradient(circle, rgba(200,150,60,.10), transparent 70%)",
        animation: "wlib-candle 3.4s ease-in-out infinite",
      }} />

      {/* ── Bookcases ── */}
      <div style={{
        maxWidth: 880, margin: "0 auto", padding: "22px 16px 80px",
        display: "flex", flexDirection: "column", gap: 34,
      }}>
        {cases.map(bc => (
          <div key={bc.id} style={{
            border: `1px solid ${GM.pageBorder || GM.inkGoldDim + "44"}`,
            borderRadius: 14,
            background: "linear-gradient(180deg, rgba(30,22,12,.35), rgba(10,8,16,.55))",
            boxShadow: "0 8px 30px rgba(0,0,0,.5), inset 0 1px 0 rgba(232,184,75,.06)",
            padding: "14px 14px 18px",
          }}>
            <div style={{
              fontSize: 11, letterSpacing: 5, fontWeight: 900,
              color: GM.inkGold, textAlign: "center", marginBottom: 12,
              fontFamily: GM.fontUI || GM.font,
            }}>
              ━ {bc.label} ━
            </div>

            {bc.rows.map((row, ri) => (
              <div key={row.subject} style={{ marginBottom: ri < bc.rows.length - 1 ? 16 : 0 }}>
                {/* Row label = the subject */}
                <div style={{
                  fontSize: 10, letterSpacing: 2, fontWeight: 800,
                  color: GM.vellumDim, marginBottom: 6, paddingLeft: 4,
                  fontFamily: GM.fontUI || GM.font,
                }}>
                  {row.subject.toUpperCase()}
                </div>

                {/* The shelf */}
                <div style={{
                  display: "flex", gap: 8, alignItems: "flex-end",
                  padding: "0 6px 8px",
                  borderBottom: `3px solid ${GM.inkGoldDim}66`,
                  borderRadius: "0 0 3px 3px",
                }}>
                  {row.books.map((book, bi) => {
                    const k = KIND[book.kind];
                    const spine = SPINES[(ri * 3 + bi + bc.id.length) % SPINES.length];
                    return (
                      <button
                        key={book.title}
                        onClick={() => setOpenBook({ subject: row.subject, book })}
                        title={book.title}
                        style={{
                          flex: 1, minWidth: 0, cursor: "pointer",
                          height: 74 + ((bi * 7 + ri * 5) % 14), // organic uneven heights
                          borderRadius: "4px 4px 2px 2px",
                          border: `1px solid ${k.color}44`,
                          borderBottom: `2px solid ${k.color}66`,
                          background: `linear-gradient(180deg, ${spine} 0%, #0c0a12 100%)`,
                          color: GM.vellum,
                          display: "flex", flexDirection: "column",
                          alignItems: "center", justifyContent: "center", gap: 4,
                          padding: "4px 6px",
                          transition: "transform .15s, box-shadow .15s",
                          boxShadow: "0 2px 6px rgba(0,0,0,.45)",
                        }}
                        onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-5px)"; e.currentTarget.style.boxShadow = `0 8px 16px rgba(0,0,0,.6), 0 0 12px ${k.color}33`; }}
                        onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 2px 6px rgba(0,0,0,.45)"; }}
                      >
                        <span style={{ fontSize: 15, color: k.color }}>{k.glyph}</span>
                        <span style={{
                          fontSize: 9.5, fontStyle: "italic", lineHeight: 1.25,
                          textAlign: "center", color: GM.vellumDim,
                          overflow: "hidden", display: "-webkit-box",
                          WebkitLineClamp: 3, WebkitBoxOrient: "vertical",
                        }}>
                          {book.title}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        ))}

        <div style={{ textAlign: "center", fontSize: 11, fontStyle: "italic", color: GM.vellumFaint }}>
          A small desk sits in the corner. A nameplate reads "WILLEE." The chair is warm. He is not in it.
        </div>
      </div>

      {/* ── Open book modal ── */}
      {openBook && (() => {
        const k = KIND[openBook.book.kind];
        return (
          <div
            onClick={() => setOpenBook(null)}
            style={{
              position: "fixed", inset: 0, zIndex: 500,
              background: "rgba(3,3,8,.82)", backdropFilter: "blur(4px)",
              display: "flex", alignItems: "center", justifyContent: "center",
              padding: 20,
            }}
          >
            <div
              onClick={e => e.stopPropagation()}
              style={{
                maxWidth: 560, width: "100%", maxHeight: "82vh", overflowY: "auto",
                borderRadius: 14,
                border: `1px solid ${k.color}66`,
                background: "linear-gradient(160deg, #17121f 0%, #0a0812 100%)",
                boxShadow: `0 0 40px ${k.color}22, 0 20px 60px rgba(0,0,0,.7)`,
                padding: "22px 26px 26px",
                animation: "wlib-book-pop .22s ease-out",
              }}
            >
              <div style={{
                fontSize: 9, letterSpacing: 4, fontWeight: 900, color: k.color,
                fontFamily: GM.fontUI || GM.font, marginBottom: 4,
              }}>
                {k.glyph} {k.label} — ON THE SUBJECT OF {openBook.subject.toUpperCase()}
              </div>
              <div style={{
                fontSize: 19, fontWeight: 900, color: GM.inkGoldBright,
                marginBottom: 14, lineHeight: 1.3,
              }}>
                {openBook.book.title}
              </div>
              <div style={{
                fontSize: 14, lineHeight: 1.85, whiteSpace: "pre-wrap",
                color: GM.vellum, fontStyle: openBook.book.kind === "song" ? "italic" : "normal",
              }}>
                {openBook.book.text}
              </div>
              <div style={{ display: "flex", justifyContent: "center", marginTop: 20 }}>
                <button
                  onClick={() => setOpenBook(null)}
                  style={{
                    padding: "8px 22px", borderRadius: 20, cursor: "pointer",
                    border: `1px solid ${GM.inkGold}55`, background: "transparent",
                    color: GM.inkGold, fontWeight: 900, fontSize: 12, letterSpacing: 2,
                    fontFamily: GM.fontUI || GM.font,
                  }}
                >
                  CLOSE THE BOOK
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
